// @flow
import { isPlainObject, has } from "../utils/helpers/objects";
import { getRepositoryResourceUrl } from "../utils/helpers/repositoryResource";
import {
  ALL_CONTENT_IN_DATA_SETTING,
  INCLUDE_TIME_OFFSET,
  INTERNAL_LOGIN_TYPE,
  LOGIN_TYPE,
  LOGIN_PATH_SETTING,
  LOGIN_USERNAME_SETTING,
  LOGIN_PASSWORD_SETTING,
  LOGOUT_PATH_SETTING,
  UPLOAD_PATH,
  CAPTCHA_PATH,
} from "./Constants";

type Setting = boolean | string | number | Array<string>;

const serverBase =
  typeof __CONTEXT_PATH__ === "undefined" ? null : __CONTEXT_PATH__;

const defaultSettings: { [name: string]: Setting } = {
  // This uses contextPath set from property from server
  CONTEXT_PATH:
    serverBase ??
    // $FlowIssue
    globalThis?.contextPath ??
    "/BeInformed",

  // Indicates if contributions should be cached
  CACHE_CONTRIBUTIONS: true,

  // Debug missing i18n messages in layout
  DEBUG_I18N_MESSAGE_NOT_FOUND: true,

  // Time to show the notification bar
  HIDE_NOTIFICATION_TIMEOUT: 5000,

  // Always commit form, does not show result pages for instruments
  ALWAYS_COMMIT_FORM: false,

  // Render instrument and event question labels on forms (might result in duplicate labels)
  RENDER_QUESTION_LABELS: true,

  // Render mandatory indications on attributes
  RENDER_MANDATORY_ATTRIBUTE_INDICATION: true,

  // Render optional indications on attributes
  RENDER_OPTIONAL_ATTRIBUTE_INDICATION: false,

  // Renders a toggle with supported list views, options are ListView and TableView
  AVAILABLE_LIST_VIEWS: ["ListView", "TableView"],

  // Toggle client side validation
  USE_CLIENTSIDE_VALIDATION: true,

  // Indicates if server validation should be used when a form field is changed
  USE_INSTANT_SERVER_VALIDATION: true,

  // Indicates which locales are enabled
  ENABLED_LOCALES: ["en", "nl"],

  // Renders the global log in page
  RENDER_GLOBAL_LOGIN_PAGE: false,

  // Renders the global log out page
  RENDER_GLOBAL_LOGOUT_PAGE: false,

  // Renders the login variants as option on the global logout page
  RENDER_MULTI_LOGIN_ON_GLOBAL_LOGOUT_PAGE: false,

  // Path to login page / component
  LOGIN_PATH: "/signin",

  // Path to administrator login page / component
  ADMINISTRATOR_LOGIN_PATH: "",

  // Path to logout page / component
  LOGOUT_PATH: "/signout",

  // Render forms in a modal
  RENDER_FORMS_IN_MODAL: true,

  // Wait timeout before rendering submit wait icon
  SHOW_SUBMIT_WAIT_TIMEOUT: 300,

  // Swim lane diagram configuration file
  SWIM_LANE_DIAGRAM_CONFIGURATION: "/Library/Diagrams/Overviews.json",

  // Business scenario concept types
  BUSINESS_SCENARIO_CONCEPT_TYPE: [
    "/Library/KMTs/Business scenarios.bixml/BusinessScenario",
  ],
  BUSINESS_SCENARIO_STEP_CONCEPT_TYPE: [
    "/Library/KMTs/Business scenarios.bixml/ScenarioStep",
  ],
  BUSINESS_SCENARIO_PERSONA_CONCEPT_TYPE: [
    "/Library/KMTs/Business scenarios.bixml/Persona",
  ],
  BUSINESS_SCENARIO_SYSTEM_CONCEPT_TYPE: [
    "/Library/KMTs/Business scenarios.bixml/System",
  ],

  // indicates on what day the week starts (0 = Sunday)
  CALENDAR_WEEK_STARTS_ON: 1,

  // indicates which date indicates the first week of the year
  CALENDAR_FIRST_WEEK_CONTAINS_DATE: 1,

  // parameters that are added for the ui state, but should not be send to the modular ui
  UI_PARAMETERS: [],

  // disable hide-when-empty hint for layouts running on Be Informed versions older than 21.1
  ALLOW_HIDE_WHEN_EMPTY_ON_TABS: true,

  // reload the complete page on signout, usefull when third party authentication is in place
  PAGE_RELOAD_ON_UNAUTHORIZED_ERROR: false,

  XHR_TIMEOUT_MS: 300000,

  XHR_ALWAYS_WITH_CREDENTIALS: false,

  HIDE_WHEN_EMPTY_IGNORE_TASKS: false,

  SHOW_AVATARS: true,

  COMPLETE_SOURCE: false,
};

let settings = defaultSettings;

/**
 */
export const setSettings = (config: Object) => {
  if (isPlainObject(config)) {
    settings = Object.assign(defaultSettings, config);
  }
};

/**
 */
export const getSetting = (key: string, defaultValue?: any): any => {
  if (!has(settings, key) && defaultValue === undefined) {
    throw new Error(
      `Setting with name ${key} not found and no defaultValue given`,
    );
  }
  return settings[key] ?? defaultValue;
};

/**
 */
export const setSetting = (key: string, value: any) => {
  settings[key] = value;
};

/**
 */
export const allSettings = (): { [name: string]: Setting } => settings;

/**
 * Indicates if contributions must be cached
 */
export const cacheContributions = (): boolean =>
  getSetting("CACHE_CONTRIBUTIONS", true);

/**
 * The context path is retrieved on the server using the context path property as set in the runtime properties
 * This can be manually overriden by setting the UI setting 'CONTEXT_PATH'
 */
export const getBasePath = (): string =>
  getSetting("CONTEXT_PATH", "/BeInformed");

/**
 * The context path to modular ui rest services
 */
export const getBasePathModularUI = (): string =>
  getSetting("CONTEXT_PATH_SERVER", getBasePath());

/**
 * Used to link the css and js files in the server templates
 */
export const getBasePathServer = (): string =>
  getSetting("CONTEXT_PATH_SERVER", getBasePath());

/**
 * Path to upload service
 */
export const getUploadPath = (
  contextPath: string = getBasePathModularUI(),
  origin: string = "",
): string => `${origin}${contextPath}${UPLOAD_PATH}`;

/**
 * Path to captcha service
 */
export const getCaptchaPath = (
  contextPath: string = getBasePathModularUI(),
  origin: string = "",
): string => `${origin}${contextPath}${CAPTCHA_PATH}`;

/**
 */
export const hasAllContentInData = (): boolean =>
  getSetting(ALL_CONTENT_IN_DATA_SETTING, true);

/**
 */
export const isIncludeTimeOffsetInDateTimes = (): boolean =>
  getSetting(INCLUDE_TIME_OFFSET, false);

/**
 */
export const getEnabledLocales = (): Array<string> =>
  getSetting("ENABLED_LOCALES", ["en"]);

/**
 * Login type
 */
export const loginType = (): string | Array<string> =>
  getSetting(LOGIN_TYPE, INTERNAL_LOGIN_TYPE.JAAS);

/**
 */
export const loginPath = (clientName: string = "FormClient"): string =>
  getSetting(LOGIN_PATH_SETTING, `/callback?client_name=${clientName}`);
/**
 */
export const loginUsernameField = (): string =>
  getSetting(LOGIN_USERNAME_SETTING, "j_username");
/**
 */
export const loginPasswordField = (): string =>
  getSetting(LOGIN_PASSWORD_SETTING, "j_password");
/**
 */
export const logoutPath = (): string =>
  getSetting(LOGOUT_PATH_SETTING, "/logout");

export const LOGIN_PATH: string = loginPath();
export const LOGOUT_PATH: string = logoutPath();

/**
 * Model overview swim lane configuration
 */
export const getSwimLaneDiagramConfiguration: Object = () => {
  let configLocation = getSetting(
    "SWIM_LANE_DIAGRAM_CONFIGURATION",
    "/Library/Diagrams/Overviews.json",
  );
  if (!configLocation.startsWith("/")) {
    configLocation = `/${configLocation}`;
  }
  return getRepositoryResourceUrl(configLocation);
};

/**
 * Set the context path for requests to be informed
 */
export const setContextPath = (contextPath: string) => {
  setSetting("CONTEXT_PATH", contextPath);
};

/**
 * Backwards compatible export of BASE constant
 */
export const BASE: string = getBasePath();
